<template>
    <div class="pb-5 flex flex-col xl:flex-row">
        <div class="flex-1 lg:mr-5">
            <p>
              The <a href="https://hextree.io/" target="_blank" class="underline hover:text-green">hextree.io</a> flash programmer is a browser-based tool for programming SPI flashes. It uses WebUSB and WebSerial to directly connect to programmers.
            </p>
            <p class="my-8"><b>Supported programmers:</b>
                <ul class="my-1 pl-4">
                    <li>• CH341a-based programmers</li>
                    <li>• FT232H-based programmers</li>
                    <li>• FT2232H-based programmers (Tigard)</li>
                    <li>• Serprog-based programmers</li>
                    <!-- <li>Raspberry Pi Pico</li> -->
                </ul>
            </p>
            <p v-if="webusb" class="rounded-lg bg-red p-3 mt-5">
                This is beta software for hackers. It might accidentally erase your flash or brick your device. Please don't attempt to flash the PLC of your nuclear power plant with this.
            </p>
        </div>
        <div class="flex-1 lg:ml-5">
            <div class="flex-1 " v-if="!webusb">
                <div class="border-red py-2 px-4 border border-1 rounded-lg w-full">
                  <b>Your browser does not support WebUSB</b><br>
                  Please use a WebUSB compatible browser such as Chrome, Edge, or Opera to connect to a physical programmer. In the meantime you can try our demo mode.
                </div>
            </div>
            <div class="mt-6">
                <VButton variant="green" v-if="webusb" @click="connect" class="mr-4">CONNECT TO USB</VButton>
                <VButton variant="dark" @click="connectDemo">ENTER DEMO MODE</VButton>
            </div>
            <div v-if="webusb" class="mt-4">
                Click <a class="text-white underline hover:text-green cursor-pointer" @click="connectSerprog" >here</a> to connect to a serprog-based programmer instead
            </div>
        </div>

        <div class="flex-1 lg:mr-5 mt-10">
            <p>
                <strong>Limitations</strong><br/>
            </p>
            <p>
                This flasher currently has a lot of limitations. If it does not work for your situation, try a tool such as <a href="https://flashprog.org" target="_blank">flashprog</a> instead.
                <ul class="my-1 pl-4">
                    <li>• No (software) write-protect support</li>
                    <li>• No chip identification beyond RDID / SFDP</li>
                    <li>• No partial writes</li>
                    <li>• Very slow erase (smallest page size)</li>
                </ul>
            </p>
        </div>
    </div>
</template>

<script>
import { DemoAdapter } from '../flash/DemoAdapter';
import { createProfiles, findProfile } from '../flash/demo/profiles';
import { USB_ADAPTERS } from '../flash/Adapters.js';
import { Serprog } from '../flash/adapters/serprog.js';
import VButton from './VButton.vue';

export default {
    name: 'ConnectAdapter',
    components: {
        VButton
    },
    data() {
        // Demo profile defaults to the first in the list. ?profile=<id>
        // overrides it (undocumented, handy for testing different chips).
        const demoProfiles = createProfiles();
        let selectedProfileId = demoProfiles[0].id;
        try {
            const params = new URLSearchParams(window.location.search);
            const fromUrl = params.get('profile');
            if (fromUrl && demoProfiles.some(p => p.id === fromUrl)) {
                selectedProfileId = fromUrl;
            }
        } catch {
            // URLSearchParams missing in some exotic environments — ignore.
        }
        return {
            adapter: null,
            demoProfiles,
            selectedProfileId,
        }
    },
    props: {},
    emits: [
        'connected',
        'next'
    ],
    computed: {
        webusb() {
            return !!navigator.usb;
        }
    },
    created() {
        console.log("YO")
    },
    mounted() {
        console.log("mounted")
        this.autoConnectIfEnabled();
    },
    methods: {
        /**
         * Attempt to connect automatically if `autoconnect` = "true" in localStorage,
         * and the device with stored vendorId/productId is available.
         */
        async autoConnectIfEnabled() {
            const autoconnect = localStorage.getItem("autoconnect");
            if (autoconnect === "true") {
                const vendorId = parseInt(localStorage.getItem("deviceVendorId") || "0", 10);
                const productId = parseInt(localStorage.getItem("deviceProductId") || "0", 10);

                // Attempt to find and connect to the previously used device
                try {
                    const devices = await navigator.usb.getDevices();
                    const device = devices.find(d => d.vendorId === vendorId && d.productId === productId);
                    if (device) {
                        console.log("Auto-connecting to last used device:", device);
                        // Identify which adapter matches this device
                        for (let i = 0; i < USB_ADAPTERS.length; i++) {
                            const adapter = USB_ADAPTERS[i];
                            for (let j = 0; j < adapter.usb_filter.length; j++) {
                                const filter = adapter.usb_filter[j];
                                if (filter.vendorId === device.vendorId && filter.productId === device.productId) {
                                    await adapter.setupDevice(device);
                                    this.adapter = adapter;
                                    this.$emit('connected', this.adapter);
                                    return;
                                }
                            }
                        }
                    }
                } catch (err) {
                    console.log("Error auto-connecting:", err);
                }
            }
        },

        /**
         * Prompts user to pick a USB device. Stores autoconnect info upon success.
         */
        async connect() {
            const usb_filter = [];
            for (let i = 0; i < USB_ADAPTERS.length; i++) {
                usb_filter.push(...USB_ADAPTERS[i].usb_filter);
            }
            console.log("Using usb filter:", usb_filter);

            let device;
            try {
                device = await navigator.usb.requestDevice({ filters: usb_filter });
            } catch (err) {
                console.log("Error requesting device:", err);
                return;
            }

            if (!device) return;
            console.log("Got device:", device);

            // Store the device info in localStorage for auto-connect
            localStorage.setItem("autoconnect", "true");
            localStorage.setItem("deviceVendorId", device.vendorId.toString());
            localStorage.setItem("deviceProductId", device.productId.toString());

            // Figure out which adapter is relevant
            for (let i = 0; i < USB_ADAPTERS.length; i++) {
                const adapter = USB_ADAPTERS[i];
                for (let j = 0; j < adapter.usb_filter.length; j++) {
                    const filter = adapter.usb_filter[j];
                    if (filter.vendorId !== device.vendorId) continue;
                    if (filter.productId !== device.productId) continue;

                    await adapter.setupDevice(device);
                    this.adapter = adapter;
                    this.$emit('connected', this.adapter);
                    return;
                }
            }
        },

        /**
         * Connects to a serprog-based programmer via WebSerial.
         * (Optionally, you could store autoconnect info here too if needed.)
         */
        async connectSerprog() {
            const port = await navigator.serial.requestPort();
            const adapter = new Serprog();
            await adapter.setupDevice(port);
            this.$emit('connected', adapter);
        },

        /**
         * Enters the demo mode without storing any autoconnect info.
         */
        connectDemo() {
            const profile = findProfile(this.demoProfiles, this.selectedProfileId)
                || this.demoProfiles[0];
            this.adapter = new DemoAdapter(profile);
            this.adapter.getDevice()
                .then(() => {
                    this.$emit('connected', this.adapter);
                })
                .catch((err) => {
                    if (err instanceof DOMException && err.name === "NotFoundError") {
                        console.log("No device selected.");
                    } else {
                        throw(err);
                    }
                });
        }
    }
};
</script>

<style scoped>
/* Add styles if needed */
</style>
